import {
  can,
  canProjectDocument,
  type FileInfo,
  PROJECT_DOCUMENT_MAX_BYTES,
  PROJECT_DOCUMENT_TYPES,
  type ProjectDocumentType,
  type ProjectSummary,
} from '@epc/contracts';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { FileLink } from '../../components/FileLink';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { date } from '../../lib/format';
import { projectsQuery } from '../../lib/projects';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { Field, Input, Select } from '../../ui/form';
import { Page, PageHeader, Panel } from '../../ui/layout';

const targetOf = (p: ProjectSummary) => ({
  projectId: p.id,
  legalEntityId: p.legalEntityId,
  directionId: p.directionId,
  projectPath: [p.parentId],
});
const sizeText = (bytes: number) =>
  bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(1)} КБ` : `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
type PendingFile = { id: string; file: File; type: ProjectDocumentType | ''; done?: boolean; error?: string };

export function ProjectDocumentsPanel({ project }: { project: ProjectSummary }) {
  const me = useMe();
  const qc = useQueryClient();
  const [pending, setPending] = useState<PendingFile[]>([]);
  const staff = !!me.tenant?.staff;
  const allowed = PROJECT_DOCUMENT_TYPES.filter((x) =>
    canProjectDocument(me.access, staff, targetOf(project), x.type),
  );
  const writable = allowed.filter((x) =>
    canProjectDocument(me.access, staff, targetOf(project), x.type, true),
  );
  const queries = useQueries({
    queries: allowed.map((x) => ({
      queryKey: ['files', x.type, project.id],
      queryFn: () => api<FileInfo[]>('/files', { query: { entityType: x.type, entityId: project.id } }),
    })),
  });
  const upload = useMutation({
    mutationFn: async (items: PendingFile[]) => {
      // Проект и категории захватываются до первого запроса: переход по странице не меняет адресата.
      const projectId = project.id;
      for (const item of items) {
        if (!item.type || item.done) continue;
        try {
          if (!/\.(pdf|xlsx)$/i.test(item.file.name)) throw new Error('Выберите PDF или XLSX');
          if (item.file.size > PROJECT_DOCUMENT_MAX_BYTES) throw new Error('Файл больше 20 МБ');
          const form = new FormData();
          form.append('entityType', item.type);
          form.append('entityId', projectId);
          form.append('file', item.file);
          await api<FileInfo>('/files', { form, method: 'POST' });
          setPending((all) =>
            all.map((p) => (p.id === item.id ? { ...p, done: true, error: undefined } : p)),
          );
          await qc.invalidateQueries({ queryKey: ['files', item.type, projectId] });
        } catch (error) {
          setPending((all) =>
            all.map((p) =>
              p.id === item.id
                ? { ...p, error: error instanceof Error ? error.message : 'Не удалось загрузить файл' }
                : p,
            ),
          );
        }
      }
    },
  });
  if (!allowed.length) return <EmptyState title="Нет доступа к документам проекта" />;
  return (
    <div className="flex min-w-0 flex-col gap-4">
      <Panel title="Файлы проекта" meta={`${project.code} · ${project.name}`}>
        <p className="mb-4 text-sm text-ink-2">
          Исходные PDF и XLSX хранятся без изменения. Повторная загрузка того же файла с тем же именем и
          доступом не создаёт дубль.
        </p>
        {queries.some((q) => q.isPending) ? <Spinner /> : null}
        {queries.map((q, index) => (
          <section key={allowed[index]!.type} className="mb-5 last:mb-0">
            <h3 className="font-semibold text-ink">{allowed[index]!.title}</h3>
            <p className="mb-2 text-sm text-ink-2">{allowed[index]!.hint}</p>
            <ErrorBox error={q.error} />
            {q.data?.length ? (
              <ul className="divide-y divide-line">
                {q.data.map((f) => (
                  <li key={f.id} className="min-w-0 py-2">
                    <FileLink
                      file={f}
                      className="inline-flex min-h-11 max-w-full items-center text-accent-text [overflow-wrap:anywhere] hover:underline"
                    />
                    <p className="text-sm text-ink-2">
                      {sizeText(f.sizeBytes)} · {date(f.createdAt)}
                      {f.uploadedBy ? ` · ${f.uploadedBy}` : ''}
                    </p>
                  </li>
                ))}
              </ul>
            ) : !q.isPending && !q.error ? (
              <p className="text-sm text-ink-2">Файлов пока нет.</p>
            ) : null}
          </section>
        ))}
      </Panel>
      {writable.length ? (
        <Panel title="Загрузить файлы" meta="PDF или XLSX, до 20 МБ каждый">
          <div className="flex min-w-0 flex-col gap-4">
            <Field label="Выбрать файлы" htmlFor="project-documents-upload">
              <Input
                id="project-documents-upload"
                type="file"
                multiple
                accept=".pdf,.xlsx"
                disabled={upload.isPending}
                className="min-h-11 max-w-full py-2"
                onChange={(e) => {
                  setPending(
                    Array.from(e.target.files ?? []).map((file) => ({
                      id: crypto.randomUUID(),
                      file,
                      type: '',
                    })),
                  );
                  e.target.value = '';
                }}
              />
            </Field>
            {pending.map((p) => (
              <div key={p.id} className="min-w-0 rounded-md border border-line p-3">
                <p className="mb-2 font-medium [overflow-wrap:anywhere]">
                  {p.file.name} · {sizeText(p.file.size)}
                </p>
                {p.done ? (
                  <p role="status" className="text-success">
                    Загружен
                  </p>
                ) : (
                  <Field label={`Доступ к ${p.file.name}`} htmlFor={`document-access-${p.id}`}>
                    <Select
                      id={`document-access-${p.id}`}
                      value={p.type}
                      disabled={upload.isPending}
                      className="min-h-11"
                      onChange={(e) =>
                        setPending((all) =>
                          all.map((f) =>
                            f.id === p.id
                              ? { ...f, type: e.target.value as ProjectDocumentType, error: undefined }
                              : f,
                          ),
                        )
                      }
                    >
                      <option value="">Выберите доступ</option>
                      {writable.map((x) => (
                        <option key={x.type} value={x.type}>
                          {x.title}
                        </option>
                      ))}
                    </Select>
                  </Field>
                )}
                {p.error ? (
                  <p role="alert" className="mt-2 text-sm text-danger">
                    {p.error}
                  </p>
                ) : null}
              </div>
            ))}
            {pending.length ? (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="primary"
                  className="min-h-11"
                  loading={upload.isPending}
                  disabled={!pending.some((p) => !p.done) || pending.some((p) => !p.done && !p.type)}
                  onClick={() => upload.mutate(pending.filter((p) => !p.done))}
                >
                  Загрузить выбранные
                </Button>
                <Button className="min-h-11" disabled={upload.isPending} onClick={() => setPending([])}>
                  Очистить список
                </Button>
              </div>
            ) : null}
          </div>
        </Panel>
      ) : null}
    </div>
  );
}

export function DocumentsPage() {
  const me = useMe();
  const search = useSearch({ strict: false }) as { project?: string };
  const navigate = useNavigate();
  const projects = useQuery(projectsQuery('all'));
  const visible = (projects.data ?? []).filter(
    (p) => me.tenant?.staff && can(me.access, 'documents.view', targetOf(p)),
  );
  const selected = visible.find((p) => p.id === search.project);
  useCrumbs(selected ? [{ label: selected.code, title: selected.name }] : []);
  return (
    <Page>
      <PageHeader
        title="Документы"
        subtitle="Исходные файлы проекта и доступ к ним"
        actions={
          <div className="flex flex-wrap gap-x-5">
            <Link to="/documents/letters" className="inline-flex min-h-11 items-center underline">
              Письма: входящие и исходящие
            </Link>
            <Link to="/documents/exec" className="inline-flex min-h-11 items-center underline">
              Исполнительная документация
            </Link>
            <Link to="/documents/journals" className="inline-flex min-h-11 items-center underline">
              Журнал работ
            </Link>
          </div>
        }
      />
      <div className="mb-4 max-w-xl">
        <Field label="Проект" htmlFor="documents-project">
          <Select
            id="documents-project"
            className="min-h-11"
            value={selected?.id ?? ''}
            onChange={(e) => navigate({ to: '/documents', search: { project: e.target.value || undefined } })}
          >
            <option value="">Выберите проект</option>
            {visible.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} · {p.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <ErrorBox error={projects.error} />
      {projects.isPending ? (
        <Spinner />
      ) : selected ? (
        <>
          <Link
            to="/projects/$projectId"
            params={{ projectId: selected.id }}
            className="mb-4 inline-flex min-h-11 items-center text-accent-text hover:underline"
          >
            Открыть проект {selected.code}
          </Link>
          <ProjectDocumentsPanel key={selected.id} project={selected} />
        </>
      ) : (
        <EmptyState
          title={
            visible.length ? 'Выберите проект для просмотра и загрузки файлов' : 'Нет доступных проектов'
          }
        />
      )}
    </Page>
  );
}
